import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  getEVCrisisReference,
  getEVCrisisAIPrediction,
  getEVCrisisRecoveryTimeline,
  syncEVCrisisRecoveryTimeline,
} from '../services/evCrisisSimulatorApi';

// ============================================================================
// INDIA EV GEOECONOMIC CRISIS SIMULATOR (data from database via API)
// ----------------------------------------------------------------------------
// Theme-aligned colors (matches app theme.css – primary, accents, muted tones)
const CHART_THEME = {
  primary: '#03396c',
  primaryDark: '#022a4d',
  primaryLight: '#0d5aa7',
  secondary: '#64748b',
  success: '#059669',
  successLight: '#d1fae5',
  risk: '#b91c1c',
  riskLight: '#fee2e2',
  warning: '#d97706',
  warningLight: '#fef3c7',
  primaryLightBg: '#cce1f5',
  grid: '#e5e7eb',
  border: '#e5e7eb',
  text: '#64748b',
};
// This file implements a single-page React app for India's EV supply chain
// crisis simulator. Reference data comes from the API (/api/ev-crisis-reference)
// backed by dedicated EV database tables — no inline mock data.
// ============================================================================

// ---------------------------------------------------------------------------
// 1. Reference data & defaults
// ---------------------------------------------------------------------------
// All reference data (minerals, segments, timelines, scenarios, OEM profiles)
// is loaded from the API via /api/ev-crisis-reference.  No inline mock data.

// Stylised policy levers. In a real system these would be backed by real
// metrics (GWh of domestic capacity, HHI of supplier diversification, etc.).
const DEFAULT_POLICY = {
  domesticGwh: 0.1, // 0–1, stylised lever for local cell capacity
  diversification: 0.1, // 0–1, stylised lever for supplier diversification
  reservesDays: 5, // days of strategic reserves / buffers
};

// ---------------------------------------------------------------------------
// 2. Formatting helpers
// ---------------------------------------------------------------------------

// Format a number as a percentage with 0 decimals. Expects 0–100, not 0–1.
function formatPercent(value) {
  if (!Number.isFinite(value)) return '–';
  return `${value.toFixed(0)}%`;
}

// Convert days into a human readable string (days/weeks/months).
function formatDays(value) {
  if (!Number.isFinite(value)) return '–';
  if (value < 1) return '< 1 day';
  if (value < 7) return `${value.toFixed(0)} days`;
  const weeks = value / 7;
  if (weeks < 4) return `${weeks.toFixed(1)} weeks`;
  const months = value / 30;
  return `${months.toFixed(1)} months`;
}

// Convert a rupee value into ₹ crore (rounded, with Indian locale separators).
function formatRupeesCrore(value) {
  if (!Number.isFinite(value)) return '–';
  const rounded = Math.round(value);
  return `₹${rounded.toLocaleString('en-IN')} Cr`;
}

// ---------------------------------------------------------------------------
// 3. Core impact model
// ---------------------------------------------------------------------------
// This function approximates the impact of supply disruptions on a single
// mineral + segment combination for a given timeline.

function computeImpact({ mineral, segment, disruptions, timelineId, timelines }) {
  if (!mineral || !segment) return null;
  const timelineList = timelines && timelines.length ? timelines : [{ id: 'immediate', adaptation: 0 }];
  const timeline = timelineList.find((t) => t.id === timelineId) || timelineList[0];

  // 1. Compute raw supply shock from supplier disruptions.
  //    Each supplier contributes its disruption severity multiplied by the
  //    share of India's exposure to that supplier.
  let rawShock = 0;
  mineral.suppliers.forEach((s) => {
    const sev = disruptions[s.id] ?? 0; // 0–1
    rawShock += sev * s.indiaExposure;
  });

  // Clamp to [0, 1] to avoid extreme values.
  rawShock = Math.min(1, Math.max(0, rawShock));

  // 2. Incorporate adaptation from timeline (longer horizon = more mitigation).
  const effectiveShock = rawShock * (1 - timeline.adaptation);

  // 3. Supply shortage (as percentage points, not 0–1).
  const supplyShortagePct = effectiveShock * 100;

  // 4. Price impact: non-linear response. As effectiveShock → 1, prices
  //    explode. The exponent (1.2) controls steepness.
  const s = Math.min(effectiveShock, 0.95); // avoid division by zero
  const priceMultiplier = Math.pow(1 / (1 - s), 1.2); // elastic response
  const priceImpactPct = (priceMultiplier - 1) * 100;

  // 5. Time to crisis: based on mineral criticality & a stylised buffer.
  //    More critical minerals have lower base buffers.
  const baseBufferDays = 60 - mineral.criticality * 20; // more critical -> lower base buffer
  const timeToCrisisDays = Math.max(3, baseBufferDays * (1 - effectiveShock * 1.1));

  // 6. Economic impact: lost EV sales and revenue.
  const annualUnits = segment.annualSalesMillion * 1_000_000;
  const avgPriceRs = segment.avgPriceLakh * 100_000; // 1 lakh = 100,000 Rs
  // Assume 60% of the effective shock translates into actual lost production.
  const lostUnits = annualUnits * effectiveShock * 0.6;
  const lostRevenueRs = lostUnits * avgPriceRs;
  const lostRevenueCrore = lostRevenueRs / 10_000_000; // 1 Cr = 10^7 Rs

  // 7. Composite risk index (0–10).
  const riskIndex = Math.min(10, 2 + effectiveShock * 8 + mineral.criticality * 2);

  return {
    supplyShortagePct,
    priceImpactPct,
    timeToCrisisDays,
    lostRevenueCrore,
    riskIndex,
    effectiveShock,
    baseBufferDays,
  };
}

// Apply policy levers and OEM buffers to adjust the base impact (stylised).
// This function does not change the underlying disruptions; it rescales the
// effective shock and derived metrics to reflect mitigation.
function adjustImpactForPolicy(baseImpact, policy, oemProfile) {
  if (!baseImpact) return null;

  const {
    effectiveShock,
    supplyShortagePct,
    timeToCrisisDays,
    lostRevenueCrore,
    riskIndex,
  } = baseImpact;

  const p = policy || DEFAULT_POLICY;
  const profile = oemProfile || { inventoryDays: 30 };

  // Policy mitigation: each lever contributes up to ~20–25% reduction in
  // effective shock. Exact values are stylised and can be recalibrated later.
  const domesticMitigation = 0.25 * (p.domesticGwh ?? 0); // 0–0.25
  const diversificationMitigation = 0.25 * (p.diversification ?? 0); // 0–0.25
  const reservesMitigation = 0.25 * Math.min(1, (p.reservesDays ?? 0) / 60); // 0–0.25

  // OEM-level mitigation via higher inventory buffers.
  // Use a steeper, more differentiated curve so OEM views diverge visibly.
  const invDays = Number(profile.inventoryDays ?? 0);
  const inventoryMitigation = Math.max(0, Math.min(0.35, (invDays - 15) / 80)); // 0..0.35

  const totalMitigation = Math.min(
    0.7, // cap total mitigation at 70% so risk never vanishes entirely
    domesticMitigation + diversificationMitigation + reservesMitigation + inventoryMitigation,
  );

  // Fallback to supplyShortagePct/100 if effectiveShock is missing.
  const originalShock = (effectiveShock ?? supplyShortagePct / 100) || 0;
  const adjustedShock = originalShock * (1 - totalMitigation);

  // Recompute key metrics based on adjusted shock.
  const adjustedSupplyShortagePct = adjustedShock * 100;
  const sAdj = Math.min(adjustedShock, 0.95);
  const priceMultiplierAdj = Math.pow(1 / (1 - sAdj), 1.2);
  const adjustedPriceImpactPct = (priceMultiplierAdj - 1) * 100;

  const shockRatio = originalShock > 0 ? adjustedShock / originalShock : 1;
  // More mitigation (lower adjusted shock) → longer time to crisis (up to a cap).
  const adjustedTimeToCrisisDays = timeToCrisisDays / Math.max(0.4, shockRatio);
  const adjustedLostRevenueCrore = lostRevenueCrore * shockRatio;
  const adjustedRiskIndex = Math.max(0, riskIndex * (1 - 0.6 * totalMitigation));

  return {
    ...baseImpact,
    effectiveShock: adjustedShock,
    supplyShortagePct: adjustedSupplyShortagePct,
    priceImpactPct: adjustedPriceImpactPct,
    timeToCrisisDays: adjustedTimeToCrisisDays,
    lostRevenueCrore: adjustedLostRevenueCrore,
    riskIndex: adjustedRiskIndex,
  };
}

// ---------------------------------------------------------------------------
// 4. Aggregation helpers (multi-segment and multi-mineral views)
// ---------------------------------------------------------------------------

// Aggregate selected EV segments into a single synthetic segment.
// segments: from API (id, label, annualSalesMillion, avgPriceLakh).
function buildAggregatedSegment(selectedIds, segments) {
  if (!segments || segments.length === 0) return null;
  if (!selectedIds || selectedIds.length === 0) return segments[0];

  if (selectedIds.includes('all')) {
    return segments.find((s) => s.id === 'all') || segments[0];
  }

  const selected = segments.filter((s) => selectedIds.includes(s.id) && s.id !== 'all');
  if (selected.length === 0) return segments[0];
  if (selected.length === 1) return selected[0];

  const totalSales = selected.reduce((sum, s) => sum + (s.annualSalesMillion || 0), 0);
  const weightedAvgPriceLakh =
    totalSales === 0
      ? selected[0].avgPriceLakh
      : selected.reduce((sum, s) => sum + (s.annualSalesMillion || 0) * (s.avgPriceLakh || 0), 0) / totalSales;

  return {
    id: 'multi-segment',
    label: `${selected.length} segments`,
    annualSalesMillion: totalSales,
    avgPriceLakh: weightedAvgPriceLakh,
  };
}

// Aggregate selected minerals into a synthetic combined mineral.
// minerals: from API (id, label, criticality, description, suppliers).
function buildAggregatedMineral(selectedIds, minerals) {
  if (!minerals || minerals.length === 0) return null;
  if (!selectedIds || selectedIds.length === 0) return minerals[0];

  const selectedMinerals = minerals.filter((m) => selectedIds.includes(m.id));
  if (selectedMinerals.length === 0) return minerals[0];
  if (selectedMinerals.length === 1) return selectedMinerals[0];

  // Merge suppliers across minerals, averaging exposure and share when they
  // appear in multiple minerals.
  const supplierMap = new Map();

  selectedMinerals.forEach((m) => {
    m.suppliers.forEach((s) => {
      if (!supplierMap.has(s.id)) {
        supplierMap.set(s.id, {
          id: s.id,
          name: s.name,
          indiaExposureSum: 0,
          globalShareSum: 0,
          count: 0,
        });
      }
      const entry = supplierMap.get(s.id);
      entry.indiaExposureSum += s.indiaExposure;
      entry.globalShareSum += s.globalShare;
      entry.count += 1;
    });
  });

  const mergedSuppliers = Array.from(supplierMap.values()).map((entry) => ({
    id: entry.id,
    name: entry.name,
    indiaExposure: entry.indiaExposureSum / entry.count,
    globalShare: entry.globalShareSum / entry.count,
  }));

  // Use the maximum criticality among the selected minerals as a conservative
  // proxy for overall criticality.
  const criticality = Math.max(...selectedMinerals.map((m) => m.criticality));

  return {
    id: 'multi-mineral',
    label: `${selectedMinerals.length} materials`,
    criticality,
    description: `Aggregated view across: ${selectedMinerals.map((m) => m.label).join(', ')}`,
    suppliers: mergedSuppliers,
  };
}

// Build disruptions from multiple active scenarios by combining their
// severities per supplier as 1 - Π(1 - sev_i). scenarios: from API.
function buildDisruptionsFromScenarios(mineral, scenarioIds, scenarios) {
  const base = {};
  if (mineral && mineral.suppliers) {
    mineral.suppliers.forEach((s) => {
      base[s.id] = 0;
    });
  }
  if (!scenarios || scenarios.length === 0 || !scenarioIds || scenarioIds.length === 0) {
    return base;
  }

  const activeScenarios = scenarios.filter((sc) => scenarioIds.includes(sc.id));

  if (mineral && mineral.suppliers) {
    mineral.suppliers.forEach((supplier) => {
      let combined = 0;
      activeScenarios.forEach((sc) => {
        let sev = 0;
        if (sc.presets === 'balanced') {
          sev = 0.3 + 0.2 * (supplier.indiaExposure || 0);
        } else {
          sev = (sc.presets && sc.presets[supplier.id]) ?? (sc.presets && sc.presets.other) ?? 0;
        }
        combined = 1 - (1 - combined) * (1 - sev);
      });
      base[supplier.id] = combined;
    });
  }
  return base;
}

// ---------------------------------------------------------------------------
// 5. Lightweight "test" helpers – internal sanity checks
// ---------------------------------------------------------------------------

function runInternalSanityChecks(minerals, segments) {
  if (!minerals?.length || !segments?.length) return;
  try {
    const mineral = minerals[0];
    const segment = segments[0];
    if (!mineral?.suppliers?.length) return;

    const disruptions1 = {};
    mineral.suppliers.forEach((s) => {
      disruptions1[s.id] = 0.5;
    });
    const impact1 = computeImpact({
      mineral,
      segment,
      disruptions: disruptions1,
      timelineId: 'immediate',
    });
    // eslint-disable-next-line no-console
    console.log('Sanity check impact (50% disruption everywhere)', impact1);

    const disruptions2 = {};
    mineral.suppliers.forEach((s) => {
      disruptions2[s.id] = 0.0;
    });
    const impact2 = computeImpact({
      mineral,
      segment,
      disruptions: disruptions2,
      timelineId: 'immediate',
    });
    // eslint-disable-next-line no-console
    console.log('Sanity check impact (0% disruption everywhere)', impact2);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Sanity checks failed', e);
  }
}

// ---------------------------------------------------------------------------
// 6. Small reusable UI primitives
// ---------------------------------------------------------------------------

// Simple slider used for policy levers and disruptions.
const Slider = ({ value, onChange }) => {
  return (
    <input
      type='range'
      min={0}
      max={100}
      value={Math.round((value ?? 0) * 100)}
      onChange={(e) => onChange(Number(e.target.value) / 100)}
      style={{ accentColor: CHART_THEME.primary }}
      className='w-full'
    />
  );
};

// Pill-style button used for toggles and multi-select options.
const PillButton = ({ active, children, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors whitespace-nowrap ${
      active
        ? 'text-white shadow'
        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 border-slate-200 dark:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'
    }`}
    style={active ? { backgroundColor: CHART_THEME.primary, borderColor: CHART_THEME.primary } : {}}
  >
    {children}
  </button>
);

// Simple card wrapper for visual grouping.
const Card = ({ title, children, extraHeaderRight }) => (
  <div className='bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-3'>
    {(title || extraHeaderRight) && (
      <div className='flex items-center justify-between gap-2'>
        {title && <h3 className='text-sm font-semibold tracking-wide text-slate-700'>{title}</h3>}
        {extraHeaderRight}
      </div>
    )}
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// 7. Narrative generator for export / copy
// ---------------------------------------------------------------------------

// Builds a concise narrative suitable for PMO/OEM briefs, based on the
// currently selected lens and impact. This is used both in the UI and for the
// "export" text area.
function buildNarrative({
  isNationalView,
  selectedOemId,
  oemProfiles,
  impact,
  aiPrediction,
  mineral,
  segment,
  scenarioLabel,
  timelineLabel,
  policy,
}) {
  if (!impact) return 'Configure a scenario to generate a narrative.';

  // Prefer AI narrative when available so export text matches on-screen analysis.
  if (aiPrediction?.summary) {
    const aiSupplyGap = aiPrediction.supply_gap_pct != null ? aiPrediction.supply_gap_pct : impact.supplyShortagePct;
    const aiPriceImpact = aiPrediction.price_impact_pct != null ? aiPrediction.price_impact_pct : impact.priceImpactPct;
    const aiTimeToCrisis = aiPrediction.time_to_crisis_days != null ? aiPrediction.time_to_crisis_days : impact.timeToCrisisDays;
    const aiEconomicImpact = aiPrediction.economic_impact_cr != null ? aiPrediction.economic_impact_cr : (impact.lostRevenueCrore || 0);
    const aiRiskIndex = aiPrediction.risk_index != null ? Number(aiPrediction.risk_index) : impact.riskIndex;

    const aiLine1 = aiPrediction.summary;
    const aiLine2 = `Under "${scenarioLabel}" for ${mineral?.label ?? 'mineral'} across ${segment?.label ?? 'segment'} over the ${timelineLabel} horizon, AI analysis indicates supply gap ~${formatPercent(aiSupplyGap)} and price impact ~+${formatPercent(aiPriceImpact)}. Estimated time-to-crisis: ${formatDays(aiTimeToCrisis)}.`;
    const aiLine3 = `Indicative annual EV revenue-at-risk is ${formatRupeesCrore(aiEconomicImpact)}, with strategic risk index at ${aiRiskIndex.toFixed(1)} / 10 under current policy and OEM buffer assumptions.`;
    const aiRecs = Array.isArray(aiPrediction.recommendations) ? aiPrediction.recommendations.filter(Boolean) : [];
    const aiLine4 = aiRecs.length > 0
      ? `Recommended actions:\n- ${aiRecs.join('\n- ')}`
      : '';

    return [aiLine1, aiLine2, aiLine3, aiLine4].filter(Boolean).join('\n\n');
  }

  const lensPrefix = isNationalView
    ? 'National lens:'
    : `OEM lens – ${(oemProfiles && oemProfiles[selectedOemId]?.label) || 'OEM'}:`;

  const line1 = `${lensPrefix} Under the scenario "${scenarioLabel}" applied to ${mineral?.label ?? 'mineral'} across ${segment?.label ?? 'segment'}, the effective supply shortage is about ${formatPercent(
    impact.supplyShortagePct,
  )} over the ${timelineLabel} horizon.`;

  const line2 = `With current policy settings (domestic cell capacity ~${Math.round(
    (policy.domesticGwh ?? 0) * 100,
  )}%, diversification effort ~${Math.round(
    (policy.diversification ?? 0) * 100,
  )}%, and strategic reserves of ~${policy.reservesDays} days), the risk index stands at ${impact.riskIndex.toFixed(
    1,
  )} / 10.`;

  const line3 = `This configuration places roughly ${formatRupeesCrore(
    impact.lostRevenueCrore || 0,
  )} of annual EV sales at risk in the selected segments, with an indicative time-to-crisis of ${formatDays(
    impact.timeToCrisisDays,
  )}.`;

  const line4 = isNationalView
    ? 'At the system level, this suggests priority for accelerated domestic manufacturing, strategic reserves for most exposed materials, and targeted diplomatic/FTA engagement with key supplier countries.'
    : 'At the firm level, this suggests strengthening supplier diversification, reviewing inventory norms for critical inputs, and aligning with national schemes on domestic cell manufacturing.';

  return [line1, line2, line3, line4].join('\n\n');
}

// ---------------------------------------------------------------------------
// 8. Main component – simulator + dashboards + scenario library
// ---------------------------------------------------------------------------

const IndiaEVCrisisSimulator = () => {
  const REF_CACHE_KEY = 'evCrisisReferenceCache.v2.hs_only';
  const REF_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
  // Reference data from database (minerals, segments, timelines, scenarios, OEMs).
  const [referenceData, setReferenceData] = useState(null);
  const [refLoading, setRefLoading] = useState(true);
  const [refError, setRefError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    try {
      const raw = window.sessionStorage.getItem(REF_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const age = Date.now() - Number(parsed?.savedAt || 0);
        if (parsed?.data && age >= 0 && age < REF_CACHE_TTL_MS) {
          if (!cancelled) {
            setReferenceData(parsed.data);
            setRefLoading(false);
          }
          return () => {
            cancelled = true;
          };
        }
      }
    } catch (_) {
      // Cache parsing failures should not block loading fresh data.
    }

    getEVCrisisReference()
      .then((data) => {
        if (cancelled) return;
        setReferenceData(data);
        try {
          window.sessionStorage.setItem(
            REF_CACHE_KEY,
            JSON.stringify({ savedAt: Date.now(), data }),
          );
        } catch (_) {
          // Ignore storage quota/unavailable errors.
        }
      })
      .catch((e) => {
        if (!cancelled) setRefError(e?.message || 'Failed to load reference data');
      })
      .finally(() => {
        if (!cancelled) setRefLoading(false);
      });
    return () => { cancelled = true; };
  }, [REF_CACHE_KEY, REF_CACHE_TTL_MS]);

  // Derived from API (fallback to empty so UI doesn't break before load).
  // Backend already provides only relevant critical materials from ev_battery_hs_cleaned.
  const minerals = referenceData?.minerals ?? [];
  const segments = useMemo(
    () => (referenceData?.segments ?? []).map((s) => ({
      ...s,
      annualSalesMillion: Number(s.annualSalesMillion ?? s.annualsalesmillion ?? 0),
      avgPriceLakh: Number(s.avgPriceLakh ?? s.avgpricelakh ?? 0),
    })),
    [referenceData],
  );
  const timelines = referenceData?.timelines ?? [];
  const scenarios = referenceData?.scenarios ?? [];
  const recoveryTimelineTemplates = referenceData?.recoveryTimelineTemplates ?? {};
  const oemProfiles = referenceData?.oemProfiles ?? {};
  const oemSegmentCoverage = referenceData?.oemSegmentCoverage ?? {};

  // High-level view: national vs OEM (vs Ola/Tata/etc.).
  const [selectedOemId, setSelectedOemId] = useState('pmo');

  // High-level page mode: detailed simulator vs compact dashboards.
  const [viewMode, setViewMode] = useState('simulator'); // 'simulator' | 'dashboard'

  // Policy levers (national & OEM).
  const [policy, setPolicy] = useState(DEFAULT_POLICY);

  // Multi-select vehicle types.
  const [selectedSegmentIds, setSelectedSegmentIds] = useState(['all']);
  // Multi-select minerals/components.
  const [selectedMineralIds, setSelectedMineralIds] = useState([]);
  // Multi-select scenarios.
  const [selectedScenarioIds, setSelectedScenarioIds] = useState(['china_export_ban']);
  // Timeline.
  const [selectedTimelineId, setSelectedTimelineId] = useState('immediate');

  // Supplier-specific disruptions and selected suppliers.
  const [disruptions, setDisruptions] = useState({}); // { supplierId: 0–1 }
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);

  // Simple scenario library – saved configurations for PMO/OEM users.
  const [scenarioLibrary, setScenarioLibrary] = useState([]); // each entry: { id, name, createdAt, snapshot }

  // Track which saved scenario (if any) is active.
  const [activeScenarioId, setActiveScenarioId] = useState(null);

  // Background monitor worker (simulated) – tracks when we "last checked" external
  // signals and whether a mock price spike is active. In a real system this would
  // poll APIs for prices, trade flows, and policy news.
  const [monitorSignals, setMonitorSignals] = useState({
    lastCheckAt: null,
    mockPriceSpike: null,
  });

  // Simple scheduled report configuration (UI only in this mock version).
  const [reportFrequency, setReportFrequency] = useState('weekly'); // 'weekly' | 'monthly'
  const [reportChannel, setReportChannel] = useState('email'); // 'email' | 'download'
  const [reportPreview, setReportPreview] = useState('');

  // Track dismissed alerts
  const [dismissedAlertIds, setDismissedAlertIds] = useState(new Set());
  const [alertCriticalityFilter, setAlertCriticalityFilter] = useState('all'); // all | critical | warning
  const [isAlertConfigOpen, setIsAlertConfigOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ maxAlerts: 10, sortMode: 'severity' });
  const [alertConfigDraft, setAlertConfigDraft] = useState({ maxAlerts: 10, sortMode: 'severity' });

  // Claude-powered prediction (Scenario Builder: analyzes EV stored data)
  const [aiPrediction, setAiPrediction] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [recoveryTimelinePoints, setRecoveryTimelinePoints] = useState([]);
  const lastTimelineSyncSignatureRef = useRef('');

  // Derived: aggregated mineral & EV segment (from database).
  const mineral = useMemo(
    () => buildAggregatedMineral(selectedMineralIds, minerals),
    [selectedMineralIds, minerals],
  );
  const segment = useMemo(
    () => buildAggregatedSegment(selectedSegmentIds, segments),
    [selectedSegmentIds, segments],
  );
  const selectedSupplierCountries = useMemo(() => {
    if (!minerals?.length || !selectedMineralIds?.length) return [];
    const chosen = minerals.filter((m) => selectedMineralIds.includes(m.id));
    const merged = new Map();
    chosen.forEach((m) => {
      (m.suppliers || []).forEach((s) => {
        const existing = merged.get(s.id);
        if (!existing) {
          merged.set(s.id, {
            id: s.id,
            name: s.name,
            indiaExposure: Number(s.indiaExposure || 0),
            globalShare: Number(s.globalShare || 0),
          });
          return;
        }
        existing.indiaExposure = Math.max(existing.indiaExposure, Number(s.indiaExposure || 0));
        existing.globalShare = Math.max(existing.globalShare, Number(s.globalShare || 0));
      });
    });
    return Array.from(merged.values()).sort((a, b) => b.indiaExposure - a.indiaExposure);
  }, [minerals, selectedMineralIds]);
  const topSupplierCountries = useMemo(
    () => selectedSupplierCountries.slice(0, 10),
    [selectedSupplierCountries],
  );

  // OEM profile and view-mode flags (from database).
  const oemProfile = oemProfiles[selectedOemId] || oemProfiles.pmo || { label: 'National view', inventoryDays: 30 };
  const isNationalView = selectedOemId === 'pmo';
  const oemSupportedSegments = oemSegmentCoverage[selectedOemId] ?? null;

  // Compute unsupported segment labels when in OEM view (for warnings).
  const unsupportedSegmentLabels = useMemo(() => {
    if (isNationalView || !oemSupportedSegments) return [];
    const effectiveSelected = selectedSegmentIds.filter((id) => id !== 'all');
    if (effectiveSelected.length === 0) return [];
    return segments.filter(
      (seg) =>
        seg.id !== 'all' &&
        effectiveSelected.includes(seg.id) &&
        !oemSupportedSegments.includes(seg.id),
    ).map((seg) => seg.label);
  }, [isNationalView, oemSupportedSegments, selectedSegmentIds, segments]);

  // Active scenarios & label for narrative (from database).
  const activeScenarios = useMemo(
    () => scenarios.filter((s) => selectedScenarioIds.includes(s.id)),
    [selectedScenarioIds, scenarios],
  );

  const scenarioLabel =
    activeScenarios.length === 1
      ? activeScenarios[0].label
      : `${activeScenarios.length} combined scenarios`;
  const primaryScenarioKey =
    activeScenarios.length > 0 ? activeScenarios[0].id : (selectedScenarioIds?.[0] || 'china_export_ban');
  const recoveryContext = useMemo(() => {
    const scenarioKey = primaryScenarioKey || 'default';
    const oemKey = selectedOemId || 'all';
    const mineralKey = selectedMineralIds.length
      ? (selectedMineralIds.includes('all') ? 'all' : [...selectedMineralIds].sort().join('+'))
      : 'all';
    const segmentKey = selectedSegmentIds.length
      ? (selectedSegmentIds.includes('all') ? 'all' : [...selectedSegmentIds].sort().join('+'))
      : 'all';
    const timelineKey = selectedTimelineId || 'all';
    return {
      scenario_key: scenarioKey,
      oem_key: oemKey,
      mineral_key: mineralKey,
      segment_key: segmentKey,
      timeline_key: timelineKey,
    };
  }, [primaryScenarioKey, selectedOemId, selectedMineralIds, selectedSegmentIds, selectedTimelineId]);

  const timelineLabel =
    timelines.find((t) => t.id === selectedTimelineId)?.label || 'selected horizon';

  // Initialise disruptions & selected suppliers whenever mineral or scenarios change.
  useEffect(() => {
    const nextDisruptions = buildDisruptionsFromScenarios(mineral, selectedScenarioIds, scenarios);
    setDisruptions((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(nextDisruptions || {});
      if (prevKeys.length !== nextKeys.length) return nextDisruptions;
      for (const k of nextKeys) {
        if ((prev?.[k] ?? 0) !== (nextDisruptions?.[k] ?? 0)) return nextDisruptions;
      }
      return prev;
    });
    const nextSupplierIds = topSupplierCountries.map((s) => s.id);
    setSelectedSupplierIds((prev) => {
      if (prev.length === nextSupplierIds.length && prev.every((id, i) => id === nextSupplierIds[i])) {
        return prev;
      }
      return nextSupplierIds;
    });
  }, [mineral, selectedScenarioIds, scenarios, topSupplierCountries]);

  // Keep selected materials aligned to currently available HS materials only.
  useEffect(() => {
    if (!minerals.length) {
      setSelectedMineralIds((prev) => (prev.length ? [] : prev));
      return;
    }
    const allowed = new Set(minerals.map((m) => m.id));
    setSelectedMineralIds((prev) => {
      const kept = (prev || []).filter((id) => allowed.has(id));
      const next = kept.length ? kept : [minerals[0].id];
      if (prev.length === next.length && prev.every((id, i) => id === next[i])) {
        return prev;
      }
      return next;
    });
  }, [minerals]);

  // Run internal sanity checks when reference data is loaded.
  useEffect(() => {
    if (minerals.length && segments.length) runInternalSanityChecks(minerals, segments);
  }, [minerals, segments]);

  // Load recovery timeline points for current simulator context (scenario + OEM + selection).
  useEffect(() => {
    let cancelled = false;
    getEVCrisisRecoveryTimeline(recoveryContext)
      .then((rows) => {
        if (cancelled) return;
        const normalized = (rows || []).map((p) => ({
          week: Number(p.week ?? 0),
          supplyRestored: Number(p.supplyRestored ?? 0),
          revenueLoss: Number(p.revenueLoss ?? 0),
        }));
        setRecoveryTimelinePoints(normalized);
      })
      .catch(() => {
        if (!cancelled) setRecoveryTimelinePoints([]);
      });
    return () => { cancelled = true; };
  }, [recoveryContext]);

  // Compute base impact from disruptions, then apply policy & OEM buffers.
  const baseImpact = useMemo(
    () => computeImpact({ mineral, segment, disruptions, timelineId: selectedTimelineId, timelines }),
    [mineral, segment, disruptions, selectedTimelineId, timelines],
  );
  const nationalProfile = oemProfiles.pmo || { label: 'National view', inventoryDays: 30 };
  const nationalImpact = useMemo(
    () => adjustImpactForPolicy(baseImpact, policy, nationalProfile),
    [baseImpact, policy, nationalProfile],
  );

  const impact = useMemo(
    () => {
      const adjusted = adjustImpactForPolicy(baseImpact, policy, oemProfile);
      if (!adjusted) return null;
      if (isNationalView || !oemSupportedSegments || oemSupportedSegments.length === 0) {
        return adjusted;
      }

      // Apply OEM segment-coverage effect directly to visible outcomes.
      // If selected segments are less represented in the OEM portfolio, revenue/risk reduce.
      const selectedIds = selectedSegmentIds.includes('all')
        ? segments.filter((s) => s.id !== 'all').map((s) => s.id)
        : selectedSegmentIds.filter((id) => id !== 'all');
      const uniqueSelected = Array.from(new Set(selectedIds));
      const supportedCount = uniqueSelected.filter((id) => oemSupportedSegments.includes(id)).length;
      const coverageRatio = uniqueSelected.length > 0 ? supportedCount / uniqueSelected.length : 1;
      const exposureFactor = 0.35 + 0.65 * coverageRatio; // keep minimum upstream exposure

      return {
        ...adjusted,
        supplyShortagePct: adjusted.supplyShortagePct * (0.6 + 0.4 * coverageRatio),
        lostRevenueCrore: adjusted.lostRevenueCrore * exposureFactor,
        riskIndex: adjusted.riskIndex * (0.65 + 0.35 * coverageRatio),
        timeToCrisisDays: adjusted.timeToCrisisDays / Math.max(exposureFactor, 0.35),
        oemCoverageRatio: coverageRatio,
        oemExposureFactor: exposureFactor,
      };
    },
    [baseImpact, isNationalView, oemProfile, oemSupportedSegments, policy, selectedSegmentIds, segments],
  );

  // Bulk disruption slider for selected suppliers – average severity of selected.
  const bulkSelectedAverage = useMemo(() => {
    if (!selectedSupplierIds.length) return 0;
    const total = selectedSupplierIds.reduce(
      (sum, id) => sum + (disruptions[id] ?? 0),
      0,
    );
    return total / selectedSupplierIds.length;
  }, [selectedSupplierIds, disruptions]);

  // Helper to format relative time
  const formatRelativeTime = (hoursAgo) => {
    if (hoursAgo < 1) return 'Just now';
    if (hoursAgo < 24) return `${Math.round(hoursAgo)} ${Math.round(hoursAgo) === 1 ? 'hour' : 'hours'} ago`;
    const days = Math.floor(hoursAgo / 24);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  };

  const backendAlerts = useMemo(() => {
    return Array.isArray(referenceData?.recentAlerts) ? referenceData.recentAlerts : [];
  }, [referenceData]);

  // Use backend EV alerts (analyzed_articles + latest NewsData.io), not synthetic alerts.
  const alerts = useMemo(() => {
    return backendAlerts;
  }, [backendAlerts]);

  const severityRank = (severity) => {
    if (severity === 'critical') return 2;
    if (severity === 'warning') return 1;
    return 0;
  };

  // Filter, sort and cap alerts based on current UI configuration.
  const activeAlerts = useMemo(() => {
    const filtered = alerts.filter((alert) => {
      if (dismissedAlertIds.has(alert.id)) return false;
      if (alertCriticalityFilter === 'all') return true;
      return alert.severity === alertCriticalityFilter;
    });

    const sorted = [...filtered].sort((a, b) => {
      const aTs = Date.parse(a.reportedAt || '') || 0;
      const bTs = Date.parse(b.reportedAt || '') || 0;
      if (alertConfig.sortMode === 'severity') {
        const bySeverity = severityRank(b.severity) - severityRank(a.severity);
        if (bySeverity !== 0) return bySeverity;
      }
      return bTs - aTs;
    });

    const maxAlerts = Math.max(1, Math.min(25, Number(alertConfig.maxAlerts) || 10));
    return sorted.slice(0, maxAlerts);
  }, [alerts, dismissedAlertIds, alertCriticalityFilter, alertConfig]);

  // Handler to dismiss an alert
  const dismissAlert = (alertId) => {
    setDismissedAlertIds((prev) => new Set([...prev, alertId]));
  };

  const openAlertConfigModal = () => {
    setAlertConfigDraft({ ...alertConfig });
    setIsAlertConfigOpen(true);
  };

  const saveAlertConfig = () => {
    const nextMax = Math.max(1, Math.min(25, Number(alertConfigDraft.maxAlerts) || 10));
    setAlertConfig({
      maxAlerts: nextMax,
      sortMode: alertConfigDraft.sortMode === 'date' ? 'date' : 'severity',
    });
    setIsAlertConfigOpen(false);
  };

  // Build a narrative string for use in the export / copy area.
  const exportNarrative = useMemo(
    () =>
      buildNarrative({
        isNationalView,
        selectedOemId,
        oemProfiles,
        impact,
        aiPrediction,
        mineral,
        segment,
        scenarioLabel,
        timelineLabel,
        policy,
      }),
    [impact, aiPrediction, isNationalView, mineral, segment, policy, scenarioLabel, selectedOemId, timelineLabel, oemProfiles],
  );

  // --------------------
  // UI event handlers
  // --------------------

  const toggleScenario = (scenario) => {
    setSelectedScenarioIds((prev) => {
      let next;
      if (prev.includes(scenario.id)) {
        next = prev.filter((id) => id !== scenario.id);
        if (next.length === 0) next = [scenario.id]; // keep at least one
      } else {
        next = [...prev, scenario.id];
      }
      setActiveScenarioId(null); // editing breaks link to saved scenario
      return next;
    });
  };

  const toggleSegment = (segId) => {
    setSelectedSegmentIds((prev) => {
      if (segId === 'all') {
        setActiveScenarioId(null);
        return ['all'];
      }
      const withoutAll = prev.filter((id) => id !== 'all');
      if (withoutAll.includes(segId)) {
        const next = withoutAll.filter((id) => id !== segId);
        setActiveScenarioId(null);
        return next.length === 0 ? ['all'] : next;
      }
      setActiveScenarioId(null);
      return [...withoutAll, segId];
    });
  };

  const toggleMineral = (mineralId) => {
    setSelectedMineralIds((prev) => {
      if (prev.includes(mineralId)) {
        const next = prev.filter((id) => id !== mineralId);
        setActiveScenarioId(null);
        return next.length === 0 ? [mineralId] : next;
      }
      setActiveScenarioId(null);
      return [...prev, mineralId];
    });
  };

  const selectAllMinerals = () => {
    setSelectedMineralIds(minerals.map((m) => m.id));
    setActiveScenarioId(null);
  };

  const toggleSupplierSelection = (id) => {
    setSelectedSupplierIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setActiveScenarioId(null);
  };

  const selectAllSuppliers = () => {
    setSelectedSupplierIds(topSupplierCountries.map((s) => s.id));
    setActiveScenarioId(null);
  };

  const handleTimelineChange = (id) => {
    setSelectedTimelineId(id);
    setActiveScenarioId(null);
  };

  const handlePolicyChange = (field, value) => {
    setPolicy((prev) => ({ ...prev, [field]: value }));
    setActiveScenarioId(null);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleOemChange = (id) => {
    setSelectedOemId(id);
  };

  // Fetch AI prediction from backend (Claude analyzes EV stored data).
  const fetchAIPrediction = () => {
    if (!mineral || !segment) return;
    setAiError(null);
    setAiLoading(true);
    const mineralKey = (mineral.id && mineral.id !== 'multi-mineral' ? mineral.id : selectedMineralIds?.[0]) ?? null;
    const segmentKey = (segment.id && segment.id !== 'multi-segment' ? segment.id : selectedSegmentIds?.[0]) ?? null;
    const payload = {
      mineral_key: mineralKey,
      segment_key: segmentKey,
      timeline_key: selectedTimelineId,
      oem_key: selectedOemId,
      scenario_keys: selectedScenarioIds || [],
      disruptions: disruptions || {},
      policy_snapshot: policy,
    };
    getEVCrisisAIPrediction(payload)
      .then((pred) => setAiPrediction(pred))
      .catch((e) => {
        setAiError(e?.message || 'AI prediction failed');
        setAiPrediction(null);
      })
      .finally(() => setAiLoading(false));
  };

  const clearAIPrediction = () => {
    setAiPrediction(null);
    setAiError(null);
  };

  // Save the current configuration into the scenario library.
  const saveCurrentScenario = () => {
    const id = `sc_${Date.now()}`;
    const name = `${isNationalView ? 'PMO' : (oemProfiles[selectedOemId]?.label || 'OEM')} – ${scenarioLabel}`;
    const createdAt = new Date().toISOString();

    const snapshot = {
      selectedOemId,
      policy,
      selectedSegmentIds,
      selectedMineralIds,
      selectedScenarioIds,
      selectedTimelineId,
      disruptions,
    };

    setScenarioLibrary((prev) => [{ id, name, createdAt, snapshot }, ...prev]);
    setActiveScenarioId(id);
  };

  // Restore a saved scenario from the library.
  const loadScenario = (entry) => {
    const { snapshot } = entry;
    setSelectedOemId(snapshot.selectedOemId);
    setPolicy(snapshot.policy);
    setSelectedSegmentIds(snapshot.selectedSegmentIds);
    setSelectedMineralIds(snapshot.selectedMineralIds);
    setSelectedScenarioIds(snapshot.selectedScenarioIds);
    setSelectedTimelineId(snapshot.selectedTimelineId);
    setDisruptions(snapshot.disruptions);
    setActiveScenarioId(entry.id);
  };

  // -----------------------------------------------------------------------
  // 9. Render helpers for dashboards
  // -----------------------------------------------------------------------

  const renderDashboardSummary = () => {
    if (!impact) {
      return (
        <p className='text-xs text-slate-500'>Configure a scenario to view dashboard metrics.</p>
      );
    }

    return (
      <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
        {/* Supply Shortage Card */}
        <div className='bg-white rounded-xl p-4 shadow-sm border border-slate-100'>
          <div className='flex items-center gap-2 mb-3'>
            <div className='w-8 h-8 rounded-full flex items-center justify-center' style={{ backgroundColor: CHART_THEME.riskLight }}>
              <svg className='w-5 h-5' style={{ color: CHART_THEME.risk }} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2.5}
                  d='M7 11l5-5m0 0l5 5m-5-5v12'
                />
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M3 12h18M3 12c.5-1 1.5-1.5 3-1.5s2.5.5 3 1.5m0 0c.5 1 1.5 1.5 3 1.5s2.5-.5 3-1.5'
                />
              </svg>
            </div>
            <div className='text-sm font-medium text-slate-700'>Supply Shortage</div>
          </div>
          <div className='text-3xl font-bold text-slate-900 mb-1'>
            {formatPercent(impact.supplyShortagePct)}
          </div>
          <p className='text-xs text-slate-500'>After adaptation & buffers</p>
        </div>

        {/* Risk Index Card */}
        <div className='bg-white rounded-xl p-4 shadow-sm border border-slate-100'>
          <div className='flex items-center gap-2 mb-3'>
            <div className='w-8 h-8 rounded-full flex items-center justify-center' style={{ backgroundColor: CHART_THEME.warningLight }}>
              <svg className='w-5 h-5' style={{ color: CHART_THEME.warning }} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                />
              </svg>
            </div>
            <div className='text-sm font-medium text-slate-700'>Risk Index</div>
          </div>
          <div className='text-3xl font-bold text-slate-900 mb-1'>
            {impact.riskIndex.toFixed(1)}/10
          </div>
          <p className='text-xs text-slate-500'>Critical threshold exceeded</p>
        </div>

        {/* Time to Crisis Card */}
        <div className='bg-white rounded-xl p-4 shadow-sm border border-slate-100'>
          <div className='flex items-center gap-2 mb-3'>
            <div className='w-8 h-8 rounded-full flex items-center justify-center' style={{ backgroundColor: CHART_THEME.primary + '20' }}>
              <svg className='w-5 h-5' style={{ color: CHART_THEME.primary }} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <div className='text-sm font-medium text-slate-700'>Time to Crisis</div>
          </div>
          <div className='text-3xl font-bold text-slate-900 mb-1'>
            {formatDays(impact.timeToCrisisDays)}
          </div>
          <p className='text-xs text-slate-500'>Review contingency measures</p>
        </div>

        {/* Revenue-at-Risk Card */}
        <div className='bg-white rounded-xl p-4 shadow-sm border border-slate-100'>
          <div className='flex items-center gap-2 mb-3'>
            <div className='w-8 h-8 rounded-full flex items-center justify-center' style={{ backgroundColor: CHART_THEME.successLight }}>
              <svg className='w-5 h-5' style={{ color: CHART_THEME.success }} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <div className='text-sm font-medium text-slate-700'>Revenue-at-Risk</div>
          </div>
          <div className='text-3xl font-bold text-slate-900 mb-1'>
            {formatRupeesCrore(impact.lostRevenueCrore || 0)}
          </div>
          <p className='text-xs text-slate-500'>Annual EV sales exposure</p>
        </div>
      </div>
    );
  };

  // Generate recovery timeline data based on current impact
  const generateRecoveryTimelineData = useMemo(() => {
    if (!impact) return [];

    const totalRevenueLoss = impact.lostRevenueCrore || 23635;
    const fallbackPoints = [
      { week: 0, supplyRestored: 0, revenueLoss: 0 },
      { week: 2, supplyRestored: 15, revenueLoss: 3000 },
      { week: 4, supplyRestored: 35, revenueLoss: 6000 },
      { week: 8, supplyRestored: 60, revenueLoss: 12000 },
      { week: 12, supplyRestored: 78, revenueLoss: 16000 },
      { week: 16, supplyRestored: 90, revenueLoss: 19000 },
      { week: 20, supplyRestored: 97, revenueLoss: 22000 },
      { week: 24, supplyRestored: 100, revenueLoss: Math.round(totalRevenueLoss) },
    ];
    const contextPoints = Array.isArray(recoveryTimelinePoints) ? recoveryTimelinePoints : [];
    const dbTemplate = recoveryTimelineTemplates?.[primaryScenarioKey]
      || recoveryTimelineTemplates?.china_export_ban
      || recoveryTimelineTemplates?.default
      || [];
    const templatePoints = contextPoints.length
      ? contextPoints
      : (dbTemplate.length
      ? dbTemplate.map((p) => ({
        week: Number(p.week ?? 0),
        supplyRestored: Number(p.supplyRestored ?? 0),
        revenueLoss: Number(p.revenueLoss ?? 0),
      }))
      : fallbackPoints);

    // Scale database/template revenue path to current computed impact.
    if (totalRevenueLoss !== 23635) {
      const scaleFactor = totalRevenueLoss / 23635;
      return templatePoints.map((point) => ({
        ...point,
        revenueLoss: Math.round(point.revenueLoss * scaleFactor),
      }));
    }

    return templatePoints;
  }, [impact, primaryScenarioKey, recoveryTimelineTemplates, recoveryTimelinePoints]);

  // Auto-sync generated recovery timeline to DB so simulator interaction updates persisted points.
  useEffect(() => {
    if (!impact || !generateRecoveryTimelineData.length) return;
    const payload = {
      ...recoveryContext,
      points: generateRecoveryTimelineData.map((p) => ({
        week: Number(p.week ?? 0),
        supplyRestored: Number(p.supplyRestored ?? 0),
        revenueLoss: Number(p.revenueLoss ?? 0),
      })),
    };
    const signature = JSON.stringify(payload);
    if (lastTimelineSyncSignatureRef.current === signature) return;
    const timer = setTimeout(() => {
      syncEVCrisisRecoveryTimeline(payload)
        .then(() => {
          lastTimelineSyncSignatureRef.current = signature;
        })
        .catch(() => {
          // Ignore non-blocking sync failures; UI keeps working with local computed series.
        });
    }, 600);
    return () => clearTimeout(timer);
  }, [impact, generateRecoveryTimelineData, recoveryContext]);

  // Calculate shock propagation data for the flowchart
  const shockPropagationData = useMemo(() => {
    if (!impact || !mineral) return null;

    // Calculate China disruption percentage
    const chinaSupplier = mineral.suppliers.find((s) => s.id === 'cn');
    const chinaDisruption = chinaSupplier ? disruptions.cn || 0 : 0;
    const chinaDisruptionPct = Math.round(chinaDisruption * 100);
    const chinaExposure = chinaSupplier ? Math.round(chinaSupplier.indiaExposure * 100) : 0;

    // Battery cell supply shortage (from impact)
    const batteryShortagePct = Math.round(impact.supplyShortagePct);

    // Calculate revenue at risk by segment
    const totalRevenueLoss = impact.lostRevenueCrore || 0;
    
    // Get segments to analyze (exclude synthetic 'all').
    const segmentsToAnalyze = segments.filter((s) => s.id !== 'all');
    const selectedBaseIds = selectedSegmentIds.includes('all')
      ? segmentsToAnalyze.map((s) => s.id)
      : selectedSegmentIds.filter((id) => id !== 'all');
    const selectedSet = new Set(selectedBaseIds);
    const supportedSet = !isNationalView && Array.isArray(oemSupportedSegments)
      ? new Set(oemSupportedSegments)
      : null;
    const activeSegments = segmentsToAnalyze.filter(
      (s) => selectedSet.has(s.id) && (!supportedSet || supportedSet.has(s.id)),
    );
    const fallbackSegments = activeSegments.length > 0 ? activeSegments : segmentsToAnalyze;

    // Calculate total revenue across active segments.
    const totalSegmentRevenue = fallbackSegments.reduce(
      (sum, s) => sum + s.annualSalesMillion * s.avgPriceLakh * 100,
      0,
    );

    const segmentLabelOverride = {
      tata: { '4w': 'Tata 4W EV' },
      ola: { '2w': 'Ola 2W EV' },
    };
    const segmentBreakdown = fallbackSegments
      .map((s) => {
        const segmentRevenue = s.annualSalesMillion * s.avgPriceLakh * 100;
        const segmentShare = totalSegmentRevenue > 0 ? segmentRevenue / totalSegmentRevenue : 0;
        return {
          id: s.id,
          name: segmentLabelOverride[selectedOemId]?.[s.id] || s.label || s.id?.toUpperCase() || 'Segment',
          revenueAtRisk: Math.round(totalRevenueLoss * segmentShare),
        };
      })
      .filter((s) => s.revenueAtRisk > 0);
    const summedImpact = segmentBreakdown.reduce((sum, s) => sum + (s.revenueAtRisk || 0), 0);

    return {
      chinaDisruptionPct,
      chinaExposure,
      batteryShortagePct,
      segmentBreakdown,
      totalImpact: summedImpact || Math.round(totalRevenueLoss),
    };
  }, [impact, mineral, disruptions, segments, selectedSegmentIds, isNationalView, oemSupportedSegments, selectedOemId]);

  // -----------------------------------------------------------------------
  // 10. JSX – main layout
  // -----------------------------------------------------------------------

  if (refLoading) {
    return (
      <div className='min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center'>
        <p className='text-slate-600 dark:text-gray-300'>Loading crisis simulator...</p>
      </div>
    );
  }
  if (refError) {
    return (
      <div className='min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center'>
        <p className='text-red-600'>Error: {refError}</p>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-gray-100 px-6 py-6 md:px-10 md:py-8 dark:[&_.text-slate-900]:text-gray-100 dark:[&_.text-slate-800]:text-gray-200 dark:[&_.text-slate-700]:text-gray-200 dark:[&_.text-slate-600]:text-gray-300 dark:[&_.text-slate-500]:text-gray-400 dark:[&_.bg-white]:!bg-slate-800 dark:[&_.bg-slate-50]:!bg-slate-700 dark:[&_.bg-slate-50\/80]:!bg-slate-700/80 dark:[&_.border-slate-100]:!border-slate-700 dark:[&_.border-slate-200]:!border-slate-600 dark:[&_.border-slate-300]:!border-slate-500'>
      <div className='max-w-6xl mx-auto space-y-6'>
        {/* Header */}
        <header className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
          <div>
            <h1 className='text-2xl md:text-3xl font-bold tracking-tight text-slate-900'>
              India EV Geoeconomic Crisis Simulator
            </h1>
            <p className='mt-1 text-sm md:text-base text-slate-600 max-w-2xl'>
              Model supply chain shocks to India's EV ecosystem across critical minerals, supplier
              countries, and timelines. Results are structured so they can be swapped for
              calibrated data when available.
            </p>
          </div>
          <div className='flex flex-col items-start md:items-end gap-2'>
            {/* View mode toggle: simulator vs dashboard */}
            <div className='flex items-center gap-2 text-xs'>
              <span className='text-slate-600 font-medium'>Mode:</span>
              <PillButton active={viewMode === 'simulator'} onClick={() => handleViewModeChange('simulator')}>
                Scenario builder
              </PillButton>
              <PillButton active={viewMode === 'dashboard'} onClick={() => handleViewModeChange('dashboard')}>
                Dashboard view
              </PillButton>
            </div>
          </div>
        </header>

        {/* View mode: National vs OEM */}
        <Card title='View: national or specific OEM'>
          <div className='flex flex-wrap gap-2'>
            {Object.entries(oemProfiles).map(([id, profile]) => (
              <PillButton
                key={id}
                active={selectedOemId === id}
                onClick={() => handleOemChange(id)}
              >
                {profile.label}
              </PillButton>
            ))}
          </div>
          <p className='mt-2 text-xs text-slate-500 max-w-3xl'>
            The national view highlights system-level exposure and macroeconomic impacts. OEM
            views approximate how a representative firm with similar sourcing patterns experiences
            the same shock, given its inventory buffers and mitigation levers.
          </p>
        </Card>

        {/* If in dashboard mode, show compact PMO/OEM view + alerts + library + export */}
        {viewMode === 'dashboard' ? (
          <>
            {/* Real-Time Alerts Section - Full Width */}
            <div className='bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-600 p-5 space-y-4'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <svg
                        className='w-5 h-5 text-slate-700'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
                        />
                      </svg>
                      <h3 className='text-sm font-semibold tracking-wide text-slate-700 dark:text-slate-100'>Real-Time Alerts</h3>
                      {activeAlerts.length > 0 && (
                        <span className='px-2 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold'>
                          {activeAlerts.length} Active
                        </span>
                      )}
                    </div>
                    <button
                      type='button'
                      className='text-[11px] text-sky-600 dark:text-sky-300 hover:text-sky-700 dark:hover:text-sky-200 font-medium'
                      onClick={openAlertConfigModal}
                    >
                      Configure alerts
                    </button>
                  </div>

                  <div className='flex flex-wrap gap-2 mb-3'>
                    <PillButton
                      active={alertCriticalityFilter === 'all'}
                      onClick={() => setAlertCriticalityFilter('all')}
                    >
                      All
                    </PillButton>
                    <PillButton
                      active={alertCriticalityFilter === 'critical'}
                      onClick={() => setAlertCriticalityFilter('critical')}
                    >
                      Critical
                    </PillButton>
                    <PillButton
                      active={alertCriticalityFilter === 'warning'}
                      onClick={() => setAlertCriticalityFilter('warning')}
                    >
                      Warning
                    </PillButton>
                  </div>

                  {activeAlerts.length === 0 ? (
                    <p className='text-xs text-slate-500 dark:text-slate-300 py-4'>
                      No recent EV alerts found from analyzed news sources.
                    </p>
                  ) : (
                    <div className='space-y-3 max-h-[460px] overflow-y-auto pr-1'>
                      {activeAlerts.map((alert) => {
                        const isCritical = alert.severity === 'critical';
                        const reportedAtMs =
                          typeof alert.reportedAt === 'number'
                            ? alert.reportedAt
                            : Date.parse(alert.reportedAt || '');
                        const hoursAgo = Number.isFinite(reportedAtMs)
                          ? (Date.now() - reportedAtMs) / (1000 * 60 * 60)
                          : 0;
                        const relativeTime = formatRelativeTime(hoursAgo);

                        return (
                          <div
                            key={alert.id}
                            className={`relative rounded-xl p-4 border ${
                              isCritical
                                ? 'bg-rose-50 border-rose-200 dark:bg-rose-900/20 dark:border-rose-700'
                                : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
                            }`}
                          >
                            <button
                              type='button'
                              onClick={() => dismissAlert(alert.id)}
                              className='absolute top-3 right-3 text-slate-400 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-100 transition-colors'
                              aria-label='Dismiss alert'
                            >
                              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M6 18L18 6M6 6l12 12'
                                />
                              </svg>
                            </button>

                            <div className='flex gap-3 pr-6'>
                              {/* Alert Icon */}
                              <div
                                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                  isCritical ? 'bg-rose-500' : 'bg-amber-500'
                                }`}
                              >
                                <svg
                                  className='w-5 h-5 text-white'
                                  fill='none'
                                  stroke='currentColor'
                                  viewBox='0 0 24 24'
                                >
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                                  />
                                </svg>
                              </div>

                              {/* Alert Content */}
                              <div className='flex-1 min-w-0 space-y-1.5'>
                                <div className='text-sm font-semibold text-slate-900 dark:text-slate-100'>
                                  {alert.title.split('**').map((part, idx) =>
                                    idx % 2 === 1 ? (
                                      <span key={idx} className='font-bold'>
                                        {part}
                                      </span>
                                    ) : (
                                      part
                                    ),
                                  )}
                                </div>
                                <div className='text-[11px] text-slate-600 dark:text-slate-300'>{relativeTime}</div>
                                {alert.sourceUrl && (
                                  <div className='text-xs text-slate-700 dark:text-slate-200'>
                                    <a
                                      href={alert.sourceUrl}
                                      target='_blank'
                                      rel='noreferrer'
                                      className='text-sky-700 dark:text-sky-300 hover:text-sky-800 dark:hover:text-sky-200 underline'
                                    >
                                      View Source
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
            </div>

            {isAlertConfigOpen && (
              <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                <div className='bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-600 w-full max-w-md p-5 space-y-4'>
                  <div className='flex items-center justify-between'>
                    <h3 className='text-sm font-semibold tracking-wide text-slate-700 dark:text-slate-100'>Configure alerts</h3>
                    <button
                      type='button'
                      onClick={() => setIsAlertConfigOpen(false)}
                      className='text-slate-400 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-100'
                    >
                      ✕
                    </button>
                  </div>

                  <div className='space-y-2'>
                    <label className='text-xs font-medium text-slate-600 dark:text-slate-200'>
                      How many alerts to show on page
                    </label>
                    <input
                      type='number'
                      min={1}
                      max={25}
                      value={alertConfigDraft.maxAlerts}
                      onChange={(e) =>
                        setAlertConfigDraft((prev) => ({
                          ...prev,
                          maxAlerts: e.target.value,
                        }))
                      }
                      className='w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100'
                    />
                  </div>

                  <div className='space-y-2'>
                    <label className='text-xs font-medium text-slate-600 dark:text-slate-200'>Sort events by</label>
                    <select
                      value={alertConfigDraft.sortMode}
                      onChange={(e) =>
                        setAlertConfigDraft((prev) => ({
                          ...prev,
                          sortMode: e.target.value,
                        }))
                      }
                      className='w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100'
                    >
                      <option value='severity'>Severity</option>
                      <option value='date'>Date</option>
                    </select>
                  </div>

                  <div className='flex justify-end gap-2 pt-2'>
                    <button
                      type='button'
                      onClick={() => setIsAlertConfigOpen(false)}
                      className='px-3 py-2 text-xs rounded-lg border border-slate-200 dark:border-slate-500 text-slate-600 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700'
                    >
                      Cancel
                    </button>
                    <button
                      type='button'
                      onClick={saveAlertConfig}
                      className='px-3 py-2 text-xs rounded-lg text-white'
                      style={{ backgroundColor: CHART_THEME.primary }}
                    >
                      Save configuration
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Business Continuity & Recovery Timeline */}
            {impact && (
              <Card title='Business Continuity & Recovery Timeline'>
                <p className='text-xs text-slate-600 mb-4'>
                  Supply restoration and cumulative revenue loss over 24-week crisis period ({scenarioLabel} scenario).
                </p>
                <div className='h-80 w-full'>
                  <ResponsiveContainer width='100%' height='100%'>
                    <LineChart
                      data={generateRecoveryTimelineData}
                      margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray='3 3' stroke={CHART_THEME.grid} />
                      <XAxis
                        dataKey='week'
                        label={{ value: 'Weeks from Crisis Start', position: 'insideBottom', offset: -5 }}
                        tick={{ fontSize: 11, fill: CHART_THEME.text }}
                        stroke={CHART_THEME.text}
                        tickMargin={8}
                      />
                      <YAxis
                        yAxisId='left'
                        label={{ value: 'Supply Restored (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                        domain={[0, 100]}
                        tick={{ fontSize: 11, fill: CHART_THEME.success }}
                        stroke={CHART_THEME.success}
                        tickMargin={8}
                      />
                      <YAxis
                        yAxisId='right'
                        orientation='right'
                        label={{ value: 'Revenue Loss (₹ Cr)', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                        domain={[0, (dataMax) => Math.ceil(dataMax / 6000) * 6000]}
                        tick={{ fontSize: 11, fill: CHART_THEME.risk }}
                        stroke={CHART_THEME.risk}
                        tickMargin={8}
                        tickFormatter={(value) => value.toLocaleString('en-IN')}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: `1px solid ${CHART_THEME.border}`,
                          borderRadius: '8px',
                          fontSize: '12px',
                          padding: '8px',
                        }}
                        formatter={(value, name) => {
                          if (name === 'supplyRestored') return [`${value}%`, 'Supply Restored'];
                          if (name === 'revenueLoss') return [formatRupeesCrore(value), 'Revenue Loss'];
                          return [value, name];
                        }}
                        labelFormatter={(label) => `Week ${label}`}
                      />
                      <Legend
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType='circle'
                        iconSize={8}
                        formatter={(value) => {
                          if (value === 'supplyRestored') return 'Supply Restored (%)';
                          if (value === 'revenueLoss') return 'Revenue Loss (₹ Cr)';
                          return value;
                        }}
                      />
                      <Line
                        yAxisId='left'
                        type='monotone'
                        dataKey='supplyRestored'
                        stroke={CHART_THEME.success}
                        strokeWidth={2.5}
                        dot={{ fill: CHART_THEME.success, r: 5, strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 7 }}
                        name='supplyRestored'
                      />
                      <Line
                        yAxisId='right'
                        type='monotone'
                        dataKey='revenueLoss'
                        stroke={CHART_THEME.risk}
                        strokeWidth={2.5}
                        dot={{ fill: CHART_THEME.risk, r: 5, strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 7 }}
                        name='revenueLoss'
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className='mt-4 p-3 rounded-lg border bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700'>
                  <p className='text-xs text-slate-700 dark:text-emerald-100 leading-relaxed'>
                    <span className='font-semibold'>Recovery Insights:</span> Supply restoration follows S-curve pattern
                    with rapid initial recovery (35% by week 4) followed by slower final-phase normalization. Total
                    revenue impact reaches {formatRupeesCrore(impact.lostRevenueCrore || 0)} by full recovery at week 24.
                  </p>
                </div>
              </Card>
            )}

            {/* Shock Propagation Flowchart */}
            {shockPropagationData && impact && (
              <Card title='Shock Propagation: Lithium Crisis → EV Impact'>
                <div className='space-y-4 py-2'>
                  {/* Top: China Lithium Supply */}
                  <div className='flex justify-center'>
                    <div className='text-white rounded-xl p-4 shadow-md max-w-[520px] w-full' style={{ backgroundColor: CHART_THEME.primaryDark }}>
                      <div className='text-xs font-medium mb-1'>China Lithium Supply</div>
                      <div className='text-3xl font-bold mb-1'>
                        {shockPropagationData.chinaDisruptionPct}% Disrupted
                      </div>
                      <div className='text-xs opacity-90'>
                        {shockPropagationData.chinaExposure}% of India's imports
                      </div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className='flex justify-center'>
                    <svg className='w-5 h-6 text-slate-400' fill='currentColor' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>

                  {/* Middle: Battery Cell Supply */}
                  <div className='flex justify-center'>
                    <div className='rounded-xl p-4 shadow-md max-w-[520px] w-full' style={{ backgroundColor: CHART_THEME.primaryLightBg, color: CHART_THEME.primary }}>
                      <div className='text-xs font-medium mb-1'>Battery Cell Supply</div>
                      <div className='text-3xl font-bold mb-1'>
                        {shockPropagationData.batteryShortagePct}% Shortage
                      </div>
                      <div className='text-xs opacity-90'>After adaptation & buffers</div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className='flex justify-center'>
                    <svg className='w-5 h-6 text-slate-400' fill='currentColor' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>

                  {/* Lower: Segment Revenue at Risk */}
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                    {shockPropagationData.segmentBreakdown.map((segment, idx) => (
                      <div key={segment.id || idx} className='rounded-xl p-3.5 shadow-sm' style={{ backgroundColor: CHART_THEME.primaryLightBg, color: CHART_THEME.primary }}>
                        <div className='text-xs font-semibold mb-1'>{segment.name}</div>
                        <div className='text-xl font-bold mb-0.5'>
                          {formatRupeesCrore(segment.revenueAtRisk)}
                        </div>
                        <div className='text-xs opacity-90'>Revenue at risk</div>
                      </div>
                    ))}
                  </div>

                  {/* Arrow */}
                  <div className='flex justify-center'>
                    <svg className='w-5 h-6 text-slate-400' fill='currentColor' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>

                  {/* Bottom: Total System Impact */}
                  <div className='flex justify-center'>
                    <div className='text-white rounded-xl p-4 shadow-md w-full max-w-3xl' style={{ backgroundColor: CHART_THEME.risk }}>
                      <div className='text-xs font-medium mb-1'>Total System Impact</div>
                      <div className='text-3xl font-bold mb-1'>
                        {formatRupeesCrore(shockPropagationData.totalImpact)}
                      </div>
                      <div className='text-xs opacity-90'>
                        Annual EV revenue at risk across all segments
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Dashboard - Full Width */}
            <Card
              title={isNationalView ? 'Dashboard' : `${oemProfiles[selectedOemId]?.label || 'OEM'} dashboard`}
            >
              {renderDashboardSummary()}
              <p className='mt-3 text-[11px] text-slate-500'>
                Lens: {isNationalView ? 'National system' : `OEM – ${oemProfiles[selectedOemId]?.label || 'OEM'}`}
                . Scenario: "{scenarioLabel}" · Material: {mineral.label} · Segments: {segment.label}.
              </p>
            </Card>

            {/* Scenario Library */}
            <Card
              title='Scenario library'
              extraHeaderRight={
                <button
                  type='button'
                  className='px-3 py-1 rounded-full text-[11px] font-medium border transition-colors hover:opacity-90'
                  style={{ borderColor: CHART_THEME.primary, color: CHART_THEME.primaryDark }}
                  onClick={saveCurrentScenario}
                >
                  Save current configuration
                </button>
              }
            >
              {scenarioLibrary.length === 0 ? (
                <p className='text-xs text-slate-500'>
                  No saved scenarios yet. Save a configuration from the current lens to build
                  an archive for PMO or OEM risk reviews.
                </p>
              ) : (
                <ul className='space-y-2 text-xs text-slate-700'>
                  {scenarioLibrary.map((entry) => (
                    <li
                      key={entry.id}
                      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg border text-[11px] ${
                        entry.id === activeScenarioId
                          ? 'bg-blue-50/80 border-[#03396c]'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className='flex-1 min-w-0'>
                        <div className='font-semibold truncate'>{entry.name}</div>
                        <div className='text-[10px] text-slate-500'>
                          Saved on {new Date(entry.createdAt).toLocaleString('en-IN')}
                        </div>
                      </div>
                      <button
                        type='button'
                        className='text-[11px] px-2 py-0.5 rounded-full border border-slate-300 hover:bg-slate-100'
                        onClick={() => loadScenario(entry)}
                      >
                        Load
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Comparative Analysis: India vs Peer Countries */}
            {impact && (
              <Card
                title='Comparative Analysis: India vs Peer Countries'
                extraHeaderRight={
                  <button
                    type='button'
                    className='px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    onClick={() => {
                      // Placeholder for add country functionality
                      // eslint-disable-next-line no-console
                      console.log('Add country clicked');
                    }}
                  >
                    Add Country
                  </button>
                }
              >
                <p className='text-xs text-slate-600 mb-6'>
                  How India's vulnerability compares to China, EU, and USA under identical China export
                  restrictions scenario
                </p>

                {/* Charts Grid */}
                <div className='grid md:grid-cols-2 gap-6 mb-6'>
                  {/* Supply Shortage Chart */}
                  <div>
                    <h4 className='text-sm font-semibold text-slate-700 mb-3'>Supply Shortage (%)</h4>
                    <div className='h-64'>
                      <ResponsiveContainer width='100%' height='100%'>
                        <BarChart
                          data={[
                            { country: 'India', value: Math.round(impact.supplyShortagePct) },
                            { country: 'China', value: 12 },
                            { country: 'EU', value: 38 },
                            { country: 'USA', value: 32 },
                          ]}
                          margin={{ top: 10, right: 10, left: 36, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray='3 3' stroke={CHART_THEME.grid} />
                          <XAxis
                            dataKey='country'
                            tick={{ fontSize: 11, fill: CHART_THEME.text }}
                            stroke={CHART_THEME.text}
                          />
                          <YAxis
                            label={{ value: 'Supply Shortage (%)', angle: -90, position: 'outsideLeft', offset: 8 }}
                            domain={[0, 60]}
                            tick={{ fontSize: 11, fill: CHART_THEME.text }}
                            stroke={CHART_THEME.text}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: `1px solid ${CHART_THEME.border}`,
                              borderRadius: '8px',
                              fontSize: '12px',
                            }}
                            formatter={(value) => [`${value}%`, 'Supply Shortage']}
                          />
                          <Bar dataKey='value' fill={CHART_THEME.primary} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Strategic Risk Index Chart */}
                  <div>
                    <h4 className='text-sm font-semibold text-slate-700 mb-3'>Strategic Risk Index (0-10)</h4>
                    <div className='h-64'>
                      <ResponsiveContainer width='100%' height='100%'>
                        <BarChart
                          data={[
                            { country: 'India', value: parseFloat(impact.riskIndex.toFixed(1)) },
                            { country: 'China', value: 2.0 },
                            { country: 'EU', value: 6.0 },
                            { country: 'USA', value: 5.0 },
                          ]}
                          margin={{ top: 10, right: 10, left: 36, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray='3 3' stroke={CHART_THEME.grid} />
                          <XAxis
                            dataKey='country'
                            tick={{ fontSize: 11, fill: CHART_THEME.text }}
                            stroke={CHART_THEME.text}
                          />
                          <YAxis
                            label={{ value: 'Strategic Risk Index (0-10)', angle: -90, position: 'outsideLeft', offset: 8 }}
                            domain={[0, 10]}
                            tick={{ fontSize: 11, fill: CHART_THEME.text }}
                            stroke={CHART_THEME.text}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: `1px solid ${CHART_THEME.border}`,
                              borderRadius: '8px',
                              fontSize: '12px',
                            }}
                            formatter={(value) => [`${value}`, 'Risk Index']}
                          />
                          <Bar dataKey='value' fill={CHART_THEME.risk} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Information Boxes */}
                <div className='grid md:grid-cols-2 gap-4'>
                  {/* India's Exposure Box */}
                  <div className='rounded-lg p-4 bg-rose-50 border border-rose-200 dark:bg-rose-900/20 dark:border-rose-700'>
                    <div className='flex items-start gap-3'>
                      <div className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0' style={{ backgroundColor: CHART_THEME.risk }}>
                        <svg className='w-5 h-5 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                          />
                        </svg>
                      </div>
                      <div className='flex-1'>
                        <h5 className='text-sm font-semibold mb-2 text-rose-700 dark:text-rose-200'>India's Exposure</h5>
                        <p className='text-xs text-slate-700 dark:text-slate-200 leading-relaxed'>
                          India faces <span className='font-bold'>4x higher shortage</span> than China and{' '}
                          <span className='font-bold'>1.3x higher</span> than EU due to greater import dependence
                          and lower domestic capacity buffers.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Recovery Timeline Box */}
                  <div className='rounded-lg p-4 bg-sky-50 border border-sky-200 dark:bg-sky-900/20 dark:border-sky-700'>
                    <div className='flex items-start gap-3'>
                      <div className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0' style={{ backgroundColor: CHART_THEME.primary }}>
                        <svg className='w-5 h-5 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                          />
                        </svg>
                      </div>
                      <div className='flex-1'>
                        <h5 className='text-sm font-semibold mb-2 text-sky-700 dark:text-sky-200'>Recovery Timeline</h5>
                        <p className='text-xs text-slate-700 dark:text-slate-200 leading-relaxed'>
                          India's time-to-crisis ({formatDays(impact.timeToCrisisDays)}) is{' '}
                          <span className='font-bold'>7x faster</span> than China's due to minimal strategic
                          reserves and concentration in Chinese cell imports.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <Card
              title='Export narrative (copy & paste)' 
              extraHeaderRight={
                <span className='text-[11px] text-slate-400'>Copy text into briefs</span>
              }
            >
              <p className='text-[11px] text-slate-600 mb-2'>
                This narrative is optimised for note sheets, board decks, or PMO briefings. You can
                tweak the language after copying.
              </p>
              <textarea
                className='w-full h-40 text-xs border border-slate-200 rounded-lg p-2 font-mono bg-slate-50'
                readOnly
                value={exportNarrative}
              />
            </Card>
          </>
        ) : (
          // --------------------------
          // Full simulator mode
          // --------------------------
          <>
            {/* Step 1: EV segment & mineral selection */}
            <div className='grid md:grid-cols-2 gap-4'>
              <Card title='1. Select EV segment(s)'>
                <div className='flex flex-wrap gap-2'>
                  {segments.map((seg) => {
                    const isActive =
                      seg.id === 'all'
                        ? selectedSegmentIds.includes('all')
                        : selectedSegmentIds.includes(seg.id);
                    return (
                      <PillButton
                        key={seg.id}
                        active={isActive}
                        onClick={() => toggleSegment(seg.id)}
                      >
                        {seg.label}
                      </PillButton>
                    );
                  })}
                </div>
                <p className='mt-3 text-xs text-slate-500'>
                  Aggregated annual sales (approx): {segment?.annualSalesMillion != null ? segment.annualSalesMillion.toFixed(2) : '–'} million
                  EVs, weighted average price ~₹{segment?.avgPriceLakh != null ? segment.avgPriceLakh.toFixed(1) : '–'} lakh per
                  vehicle.
                </p>
                {!isNationalView && unsupportedSegmentLabels.length > 0 && (
                  <p className='mt-1 text-[11px] text-amber-700'>
                    {oemProfiles[selectedOemId]?.label || 'OEM'} does not currently manufacture{' '}
                    {unsupportedSegmentLabels.join(', ')}. Impact values for these segments reflect
                    a hypothetical or upstream exposure scenario rather than current production.
                  </p>
                )}
              </Card>

              <Card title='2. Choose critical material(s) / component(s)'>
                <div className='flex flex-wrap gap-2 mb-3'>
                  <PillButton
                    active={minerals.length > 0 && selectedMineralIds.length === minerals.length}
                    onClick={selectAllMinerals}
                  >
                    All materials
                  </PillButton>
                  {minerals.map((m) => (
                    <PillButton
                      key={m.id}
                      active={selectedMineralIds.includes(m.id)}
                      onClick={() => toggleMineral(m.id)}
                    >
                      {m.label}
                    </PillButton>
                  ))}
                </div>
                <p className='text-xs text-slate-600 leading-relaxed'>{mineral.description}</p>
                <p className='mt-2 text-xs text-slate-500'>
                  India-dependence-weighted supplier mix:{' '}
                  {topSupplierCountries
                    .map((s) => `${s.name} ${Math.round(s.indiaExposure * 100)}%`)
                    .join(' · ')}
                </p>
              </Card>
            </div>

            {/* Step 2: Scenarios & supplier disruptions */}
            <div className='grid lg:grid-cols-[1.2fr,1fr] gap-4 items-start'>
              <Card title='3. Supplier disruptions'>
                <p className='text-xs text-slate-600 mb-3'>
                  Adjust disruption severity for each supplier (0–100%). Use the preset scenarios on
                  the right to quickly explore China export controls, shipping chokepoints, or
                  balanced stress tests. Use the bulk slider to change all selected suppliers at
                  once.
                </p>

                {/* Supplier selection + bulk slider */}
                <div className='mb-3 border border-dashed border-slate-200 rounded-xl px-3 py-2 bg-slate-50'>
                  <div className='flex items-center justify-between mb-2'>
                    <div className='flex items-center gap-2'>
                      <span className='text-[11px] font-semibold text-slate-600'>Suppliers:</span>
                      <button
                        type='button'
                        className='text-[11px] px-2 py-0.5 rounded-full border border-slate-300 bg-white hover:bg-slate-50'
                        onClick={selectAllSuppliers}
                      >
                        Select all
                      </button>
                    </div>
                    <span className='text-[11px] text-slate-500'>
                      Bulk disruption (selected): {formatPercent(bulkSelectedAverage * 100)}
                    </span>
                  </div>
                  <Slider
                    value={bulkSelectedAverage}
                    onChange={(val) => {
                      setDisruptions((prev) => {
                        const next = { ...prev };
                        selectedSupplierIds.forEach((id) => {
                          next[id] = val;
                        });
                        return next;
                      });
                    }}
                  />
                </div>

                <div className='space-y-3 max-h-[420px] overflow-y-auto pr-1'>
                  {topSupplierCountries.map((s) => {
                    const sev = disruptions[s.id] ?? 0;
                    const supplierSelected = selectedSupplierIds.includes(s.id);
                    return (
                      <div
                        key={s.id}
                        className='border border-slate-100 rounded-xl px-3 py-2.5 bg-slate-50/80 flex flex-col gap-1'
                      >
                        <div className='flex items-baseline justify-between gap-2'>
                          <div className='flex items-start gap-2'>
                            <input
                              type='checkbox'
                              className='mt-1 h-3 w-3'
                              checked={supplierSelected}
                              onChange={() => toggleSupplierSelection(s.id)}
                            />
                            <div>
                              <div className='text-sm font-medium text-slate-800'>{s.name}</div>
                              <div className='text-[11px] text-slate-500'>
                                India exposure ~{Math.round(s.indiaExposure * 100)}% · Global share
                                ~{Math.round(s.globalShare * 100)}%
                              </div>
                            </div>
                          </div>
                          <div className='text-xs font-semibold text-slate-700'>
                            {formatPercent(sev * 100)}
                          </div>
                        </div>
                        <Slider
                          value={sev}
                          onChange={(val) =>
                            setDisruptions((prev) => ({
                              ...prev,
                              [s.id]: val,
                            }))
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </Card>

              <Card title='Preset crisis scenarios (multi-select)'>
                <div className='space-y-2 mb-3'>
                  {scenarios.map((sc) => {
                    const isActive = selectedScenarioIds.includes(sc.id);
                    return (
                      <button
                        key={sc.id}
                        type='button'
                        className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                          isActive
                            ? 'bg-sky-600 text-white border-sky-600 shadow'
                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                        }`}
                        onClick={() => toggleScenario(sc)}
                      >
                        <div className='font-semibold text-[13px] mb-0.5'>{sc.label}</div>
                        <div className='text-[11px] opacity-80'>{sc.description}</div>
                      </button>
                    );
                  })}
                </div>
                <p className='text-[11px] text-slate-500'>
                  You can combine multiple scenarios (e.g., China export restrictions + shipping
                  chokepoint). Disruptions are combined as if probabilities stack (1 − Π(1 − shock)).
                </p>
              </Card>
            </div>

            {/* Step 3: Timeline selection */}
            <Card title='4. Crisis timeline'>
              <div className='flex flex-wrap gap-2 mb-3'>
                {timelines.map((t) => (
                  <PillButton
                    key={t.id}
                    active={t.id === selectedTimelineId}
                    onClick={() => handleTimelineChange(t.id)}
                  >
                    {t.label}
                  </PillButton>
                ))}
              </div>
              <p className='text-xs text-slate-600 max-w-3xl'>
                Timelines control how much the system is assumed to adapt (alternative suppliers,
                policy response, recycling, local manufacturing). Longer horizons slightly reduce
                effective disruption but cannot fully offset deep concentration risks.
              </p>
            </Card>

            {/* Policy levers (national & OEM) */}
            <Card title='5. Policy levers'>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4 text-xs'>
                <div>
                  <div className='font-semibold mb-1'>Domestic cell manufacturing (relative scale)</div>
                  <Slider
                    value={policy.domesticGwh}
                    onChange={(v) => handlePolicyChange('domesticGwh', v)}
                  />
                  <p className='mt-1 text-[11px] text-slate-500'>
                    Higher values represent larger local gigafactory capacity coming online (PLI,
                    state incentives). In this prototype, it dampens effective exposure to import
                    shocks.
                  </p>
                </div>
                <div>
                  <div className='font-semibold mb-1'>Supplier diversification</div>
                  <Slider
                    value={policy.diversification}
                    onChange={(v) => handlePolicyChange('diversification', v)}
                  />
                  <p className='mt-1 text-[11px] text-slate-500'>
                    Captures the effect of FTAs, offtake agreements, and alternate sourcing beyond
                    concentrated suppliers. Higher values reduce reliance on any single country.
                  </p>
                </div>
                <div>
                  <div className='font-semibold mb-1'>Strategic reserves (days of cover)</div>
                  <Slider
                    value={Math.min(1, (policy.reservesDays ?? 0) / 60)}
                    onChange={(v) => handlePolicyChange('reservesDays', Math.round(v * 60))}
                  />
                  <p className='mt-1 text-[11px] text-slate-500'>
                    Represents centrally held or OEM-level reserves of key materials/components.
                    Values closer to the right imply buffers of ~2 months or more in this
                    model.
                  </p>
                </div>
              </div>
            </Card>

            {/* Step 5: Impact analysis + narrative */}
            <div className='grid md:grid-cols-[1.15fr,0.85fr] gap-4 items-start'>
              <Card title='6. Impact analysis'>
                {!impact ? (
                  <p className='text-sm text-slate-500'>Configure a scenario to view results.</p>
                ) : (
                  <div className='space-y-4'>
                    {/* View context label */}
                    <div className='flex items-center justify-between text-[11px] text-slate-500'>
                      <div>
                        View lens:{' '}
                        {isNationalView ? (
                          <span className='font-semibold text-slate-700'>National system</span>
                        ) : (
                          <span className='font-semibold text-slate-700'>
                            OEM – {oemProfiles[selectedOemId]?.label || 'OEM'}
                          </span>
                        )}
                      </div>
                      {!isNationalView && (
                        <div className='italic'>
                          Metrics approximate firm-level exposure, not national GDP loss.
                        </div>
                      )}
                    </div>

                    {/* Model estimate + Claude integration */}
                    <div className='flex flex-wrap items-center gap-2'>
                      <span className='text-[11px] uppercase tracking-wide text-slate-500 font-semibold'>
                        Model estimate (formula-based)
                      </span>
                      <button
                        type='button'
                        onClick={fetchAIPrediction}
                        disabled={aiLoading}
                        className='px-3 py-1.5 rounded-lg text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        {aiLoading ? 'Analyzing…' : 'Analyze with AI'}
                      </button>
                      {aiPrediction && (
                        <button
                          type='button'
                          onClick={clearAIPrediction}
                          className='px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50'
                        >
                          Clear AI
                        </button>
                      )}
                      {aiError && (
                        <span className='text-xs text-rose-600'>{aiError}</span>
                      )}
                    </div>

                    <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                      <div className='bg-slate-50 rounded-xl p-3'>
                        <div className='text-[11px] uppercase tracking-wide text-slate-500 font-semibold'>
                          {isNationalView
                            ? 'Supply shortage (system level)'
                            : 'Supply shortage (OEM lens)'}
                        </div>
                        <div className='mt-1 text-xl font-semibold text-slate-900'>
                          {formatPercent(impact.supplyShortagePct)}
                        </div>
                        <p className='mt-1 text-[11px] text-slate-500'>
                          {isNationalView ? (
                            <>
                              Effective shock across {mineral.label.toLowerCase()} supply into India
                              after adaptation and policy mitigation.
                            </>
                          ) : (
                            <>
                              Indicative disruption in access to {mineral.label.toLowerCase()} for
                              this OEM, after accounting for its inventory buffers and system-wide
                              policy mitigation.
                            </>
                          )}
                        </p>
                      </div>

                      <div className='bg-slate-50 rounded-xl p-3'>
                        <div className='text-[11px] uppercase tracking-wide text-slate-500 font-semibold'>
                          Price impact (cells/components)
                        </div>
                        <div className='mt-1 text-xl font-semibold text-rose-700'>
                          +{formatPercent(impact.priceImpactPct)}
                        </div>
                        <p className='mt-1 text-[11px] text-slate-500'>
                          Non-linear price response; shortages above ~40% can push prices
                          sharply higher for batteries and key components.
                        </p>
                      </div>

                      <div className='bg-slate-50 rounded-xl p-3'>
                        <div className='text-[11px] uppercase tracking-wide text-slate-500 font-semibold'>
                          {isNationalView ? 'Time to national stress' : 'Time to OEM crisis'}
                        </div>
                        <div className='mt-1 text-xl font-semibold text-slate-900'>
                          {formatDays(impact.timeToCrisisDays)}
                        </div>
                        <p className='mt-1 text-[11px] text-slate-500'>
                          {isNationalView ? (
                            <>
                              After this point, EV production in the selected segments faces
                              material curtailment at the system level without emergency
                              interventions.
                            </>
                          ) : (
                            <>
                              Approximates when this OEM’s EV production lines associated with the
                              selected segments would face binding material shortages under this
                              shock.
                            </>
                          )}
                        </p>
                      </div>

                      <div className='bg-slate-50 rounded-xl p-3'>
                        <div className='text-[11px] uppercase tracking-wide text-slate-500 font-semibold'>
                          {isNationalView
                            ? 'Annual EV revenue-at-risk (national)'
                            : 'Annual EV revenue-at-risk (OEM)'}
                        </div>
                        <div className='mt-1 text-xl font-semibold text-slate-900'>
                          {formatRupeesCrore(impact.lostRevenueCrore || 0)}
                        </div>
                        <p className='mt-1 text-[11px] text-slate-500'>
                          {isNationalView ? (
                            <>
                              Indicative loss in EV sales across India in the selected segments,
                              excluding upstream mining and wider macroeconomic feedbacks.
                            </>
                          ) : (
                            <>
                              Estimate of this OEM's EV sales at risk in the selected
                              segments, assuming partial demand deferral and some scope to pass
                              through costs.
                            </>
                          )}
                        </p>
                        {!isNationalView && nationalImpact && (
                          <p className='mt-1 text-[11px] text-slate-600 dark:text-slate-200'>
                            National baseline: {formatRupeesCrore(nationalImpact.lostRevenueCrore || 0)}{' '}
                            ({Math.round(((impact.lostRevenueCrore || 0) / Math.max(nationalImpact.lostRevenueCrore || 1, 1)) * 100)}%
                            of national).
                          </p>
                        )}
                        {!isNationalView && impact?.oemCoverageRatio != null && (
                          <p className='mt-1 text-[11px] text-slate-600 dark:text-slate-200'>
                            OEM segment coverage factor: {(impact.oemCoverageRatio * 100).toFixed(0)}% supported segments selected.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className='border border-slate-100 dark:border-slate-600 rounded-xl p-3 bg-gradient-to-r from-amber-50 via-rose-50 to-sky-50 dark:from-slate-700 dark:via-slate-700 dark:to-slate-800'>
                      <div className='flex items-center justify-between gap-2 mb-1.5'>
                        <div className='text-[11px] uppercase tracking-wide text-slate-600 dark:text-slate-200 font-semibold'>
                          Strategic risk index
                        </div>
                        <div className='text-sm font-semibold text-slate-800 dark:text-slate-100'>
                          {impact.riskIndex.toFixed(1)} / 10
                        </div>
                      </div>
                      <div className='h-2 w-full bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden'>
                        <div
                          className='h-full bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-600'
                          style={{ width: `${(impact.riskIndex / 10) * 100}%` }}
                        />
                      </div>
                      <p className='mt-2 text-[11px] text-slate-600 dark:text-slate-200'>
                        Combines disruption severity, India's import ratio, and material
                        criticality, adjusted for current policy and OEM buffers. Scores above 7
                        suggest areas that warrant strategic reserves, accelerated domestic
                        manufacturing, or bilateral supply partnerships.
                      </p>
                    </div>

                    {/* AI prediction (Claude analyzes EV stored data) */}
                    {aiPrediction && (
                      <div className='mt-4 pt-4 border-t border-sky-200 bg-sky-50/50 rounded-xl p-4'>
                        <div className='flex items-center gap-2 mb-3'>
                          <span className='text-sm font-semibold text-sky-800'>AI analysis</span>
                          <span className='text-[11px] text-sky-600'>
                            {aiPrediction.source === 'claude' ? '(from EV tables + Claude)' : '(unavailable)'}
                          </span>
                          {aiPrediction.risk_level && aiPrediction.risk_level !== 'unknown' && (
                            <span className='px-2 py-0.5 rounded text-[11px] font-medium bg-sky-100 text-sky-800'>
                              {aiPrediction.risk_level}
                            </span>
                          )}
                        </div>
                        {aiPrediction.summary && (
                          <p className='text-xs text-slate-700 mb-3'>{aiPrediction.summary}</p>
                        )}
                        {aiPrediction.source === 'claude' && (
                          <div className='grid grid-cols-2 md:grid-cols-5 gap-2 mb-3 text-[11px]'>
                            {aiPrediction.supply_gap_pct != null && (
                              <div>
                                <span className='text-slate-500'>Supply gap:</span>{' '}
                                <span className='font-semibold'>{formatPercent(aiPrediction.supply_gap_pct)}</span>
                              </div>
                            )}
                            {aiPrediction.price_impact_pct != null && (
                              <div>
                                <span className='text-slate-500'>Price impact:</span>{' '}
                                <span className='font-semibold'>+{formatPercent(aiPrediction.price_impact_pct)}</span>
                              </div>
                            )}
                            {aiPrediction.time_to_crisis_days != null && (
                              <div>
                                <span className='text-slate-500'>Time to crisis:</span>{' '}
                                <span className='font-semibold'>{formatDays(aiPrediction.time_to_crisis_days)}</span>
                              </div>
                            )}
                            {aiPrediction.economic_impact_cr != null && (
                              <div>
                                <span className='text-slate-500'>Impact (Cr):</span>{' '}
                                <span className='font-semibold'>{formatRupeesCrore(aiPrediction.economic_impact_cr)}</span>
                              </div>
                            )}
                            {aiPrediction.risk_index != null && (
                              <div>
                                <span className='text-slate-500'>Risk index:</span>{' '}
                                <span className='font-semibold'>{Number(aiPrediction.risk_index).toFixed(1)}/10</span>
                              </div>
                            )}
                          </div>
                        )}
                        {aiPrediction.recommendations && aiPrediction.recommendations.length > 0 && (
                          <div>
                            <div className='text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1'>
                              Recommendations
                            </div>
                            <ul className='list-disc pl-4 text-xs text-slate-700 space-y-0.5'>
                              {aiPrediction.recommendations.map((rec, i) => (
                                <li key={i}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card>

              <Card title='Strategic narrative & policy insights'>
                {!impact ? (
                  <p className='text-sm text-slate-500'>
                    As you configure a scenario, this panel summarises implications either for the
                    national system view or for a representative OEM, based on the lens
                    selected above.
                  </p>
                ) : (
                  <>
                    {aiPrediction?.summary && (
                      <div className='mb-3 rounded-xl border border-sky-200 bg-sky-50 p-3'>
                        <div className='space-y-3 text-xs text-slate-700 leading-relaxed'>
                          <p>{aiPrediction.summary}</p>
                          <p>
                            Under <span className='font-semibold'>{scenarioLabel}</span> for{' '}
                            <span className='font-semibold'>{mineral.label}</span> over the{' '}
                            <span className='font-semibold'>{timelineLabel}</span> horizon, AI analysis
                            indicates a supply gap of approximately{' '}
                            <span className='font-semibold'>
                              {formatPercent(
                                aiPrediction.supply_gap_pct != null
                                  ? aiPrediction.supply_gap_pct
                                  : impact.supplyShortagePct,
                              )}
                            </span>{' '}
                            with a price impact near{' '}
                            <span className='font-semibold'>
                              +
                              {formatPercent(
                                aiPrediction.price_impact_pct != null
                                  ? aiPrediction.price_impact_pct
                                  : impact.priceImpactPct,
                              )}
                            </span>
                            . The estimated time-to-crisis is{' '}
                            <span className='font-semibold'>
                              {formatDays(
                                aiPrediction.time_to_crisis_days != null
                                  ? aiPrediction.time_to_crisis_days
                                  : impact.timeToCrisisDays,
                              )}
                            </span>
                            .
                          </p>
                          <p>
                            This maps to an indicative annual EV revenue-at-risk of{' '}
                            <span className='font-semibold'>
                              {formatRupeesCrore(
                                aiPrediction.economic_impact_cr != null
                                  ? aiPrediction.economic_impact_cr
                                  : impact.lostRevenueCrore || 0,
                              )}
                            </span>{' '}
                            and a strategic risk score around{' '}
                            <span className='font-semibold'>
                              {(
                                aiPrediction.risk_index != null
                                  ? Number(aiPrediction.risk_index)
                                  : impact.riskIndex
                              ).toFixed(1)}{' '}
                              / 10
                            </span>
                            , consistent with the configured policy and OEM buffer assumptions.
                          </p>
                        </div>
                        {aiPrediction.recommendations && aiPrediction.recommendations.length > 0 && (
                          <ul className='list-disc pl-4 mt-2 text-xs text-slate-700 leading-relaxed space-y-1'>
                            {aiPrediction.recommendations.map((rec, i) => (
                              <li key={i}>{rec}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    {!aiPrediction?.summary && (
                      <p className='text-sm text-slate-500'>
                        Run AI analysis to generate the strategic narrative and policy insights for
                        this simulation.
                      </p>
                    )}
                  </>
                )}
              </Card>
            </div>

            {/* Export narrative helper inside simulator as well (same text as dashboard) */}
            <Card
              title='Export narrative (copy & paste)'
              extraHeaderRight={
                <span className='text-[11px] text-slate-400'>Prototype – copy text into briefs</span>
              }
            >
              <textarea
                className='w-full h-32 text-xs border border-slate-200 rounded-lg p-2 font-mono bg-slate-50'
                readOnly
                value={exportNarrative}
              />
            </Card>
          </>
        )}

      </div>
    </div>
  );
};

export default IndiaEVCrisisSimulator;
